import { useMemo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useCheckPermission } from './permissions';

import * as Form from '@xvii/useform'
import {
  createDomain,
  selectCreateFormError,
  selectCreateFormEntityErrors,
  resetCreateFormState,
  deleteDomainAction
} from 'cityads-v3/reducers/domainParking'
import { useDialogActions } from './dialog';
import { RadioFieldItem } from 'cityads-v3/components/RadioField/RadioField';


export enum ParkedDomainStatusId {
  Pending = 1,
  RetryingDnsCheck = 2,
  RetryingRegistration = 3,
  Failed = 4,
  Successful = 5,
}

export enum ParkedDomainType {
  links = 0,
  showcases = 1,
}


/**
 * Набор столбцов таблицы припаркованных доменов
 */
export function useTableColumns() {
  const { t } = useTranslation(['DomainParking']);
  const columns = useMemo(() => ([
    { name: 'domain', title: t('fields.domain') },
    { name: 'comment', title: t('fields.comment'), wrap: true },
    { name: 'formattedDomainType', title: t('fields.domainType'), wrap: true },
    { name: 'formattedAddedAt', title: t('fields.addedAt') },
    { name: 'formattedStatus', title: t('fields.status'), wrap: true },
    { name: 'actions', title: ' ' }
  ]), [t]);

  return columns;
}


/**
 * Обработка данных формы добавления домена
 */
type useAddDomainFormProps = {
  domainType: ParkedDomainType
}
export function useAddDomainForm({ domainType }: useAddDomainFormProps) {
  const { t } = useTranslation(['DomainParking', 'main'])
  const checkPermission = useCheckPermission();
  const dispatch = useDispatch();
  const formError = useSelector(selectCreateFormError)
  const formEntityErrors = useSelector(selectCreateFormEntityErrors)

  const form = Form.useForm({
    onSubmit: async ({ domain, comment, domainType }) => {
      form.setState(Form.FormState.SENDING)
      try {
        await dispatch(createDomain(domain, comment, domainType))
        form.setState(Form.FormState.SUCCESS)
      } catch (e) {
        form.setState(Form.FormState.FAIL)
      }
    },
    fields: {
      domainType: {
        defaultValue: domainType,
        valueType: ParkedDomainType.links,
      },
      domain: {
        defaultValue: '',
        valueType: String(),
        validators: [
          Form.domain({ errorText: t('main:forms.errorFieldDomain') })
        ]
      },
      comment: {
        defaultValue: '',
        valueType: String(),
      },
    }
  })

  const resetForm = useCallback(() => {
    form.fields.domain.setValue('');
    form.fields.comment.setValue('');
    form.fields.domainType.setValue(domainType);
    form.reset();
    dispatch(resetCreateFormState(null))
  }, [domainType])

  const domainTypes: RadioFieldItem<number>[] = [
    {
      name: t('type.links'),
      value: ParkedDomainType.links
    },
    ...(checkPermission('edit_showcases') ? [
      {
        name: t('type.showcases'),
        value: ParkedDomainType.showcases
      }
    ] : [])
  ]

  return {
    domainTypes,
    form,
    formError,
    formEntityErrors,
    resetForm,
  }
}


/**
 * Удаление домена с сопутствующим отображением диалога подтверждения
 */
export function useDeleteDialog(onSuccess?: () => void) {
  const dispatch = useDispatch();
  const [domainId, setDomainId] = useState<number | undefined>();
  const [confirmOpen, handleConfirmOpen, handleConfirmClose] = useDialogActions();

  const openDeleteDialog = useCallback((domainId: number) => () => {
    setDomainId(domainId);
    handleConfirmOpen();
  }, [])

  const deleteDomain = useCallback(async () => {
    try {
      await dispatch(deleteDomainAction(domainId))
      handleConfirmClose?.()
      onSuccess?.()
    } catch (e) {
      handleConfirmClose?.()
    }
  }, [domainId])

  return {
    confirmOpen,
    openDeleteDialog,
    closeDeleteDialog: handleConfirmClose,
    domainId,
    setDomainId,
    deleteDomain
  }
}
